"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsx } from 'react/jsx-runtime';
import { cn } from '../../helpers/cn.mjs';
import { withSlotScopeClasses } from '../../helpers/scoped-slots.mjs';
import { forwardRef } from 'react';
import { tv } from 'tailwind-variants';
import { SvgIcon } from '../SvgIcon/index.mjs';

const dataBuilderCellRecipe = tv({
  slots: withSlotScopeClasses("data-builder-cell", {
    root: [
      "overflow-x-hidden flex flex-col items-start justify-center",
      "px-2.5 py-1.5 min-h-[2.125rem] bg-bg-default",
      "border-y border-r border-l-0 border-solid border-border-subtle",
      "first:border-l first:rounded-bl-[0.625rem] first:rounded-tl-[0.625rem]",
      "last:rounded-br-[0.625rem] last:rounded-tr-[0.625rem]"
    ]
  }),
  variants: {
    type: {
      name: { root: "grow-0 shrink-0 flex-nowrap self-stretch min-w-40" },
      combobox: { root: "flex-row flex-nowrap justify-start items-center px-0 py-0" },
      input: { root: "px-2 py-2 [&>input]:w-full" },
      value: { root: "py-2" },
      icon: {
        root: "grow-0 shrink-0 flex-nowrap flex items-center justify-center text-fg-subtle px-2.5 py-2.5 [&_svg]:w-3.5 [&_svg]:h-3.5"
      }
    },
    disabled: {
      true: { root: "pointer-events-none bg-bg-subtle" }
    },
    span: {
      shrink: { root: "grow-0 shrink" },
      grow: { root: "grow" }
    }
  }
});
const Cell = forwardRef(
  ({ children, disabled, className, type, span, icon, ...props }, ref) => {
    const styles = dataBuilderCellRecipe({ type, disabled, span });
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: styles.root({ className }),
        "aria-disabled": disabled,
        "aria-readonly": disabled,
        ...props,
        ref,
        children: icon ? /* @__PURE__ */ jsx(SvgIcon, { type: icon }) : children
      }
    );
  }
);
Cell.displayName = "Cell";
const dataBuilderCellButtonRecipe = tv({
  slots: withSlotScopeClasses("data-builder-cell-button", {
    root: "appearance-none bg-bg-default cursor-pointer align-middle hover:bg-bg-subtle active:bg-bg-muted"
  })
});
const CellButton = forwardRef(({ children, disabled, className, type, icon, ...props }, ref) => {
  const styles = dataBuilderCellRecipe({ type, disabled });
  const buttonStyles = dataBuilderCellButtonRecipe();
  return /* @__PURE__ */ jsx(
    "button",
    {
      className: cn(styles.root(), buttonStyles.root(), className),
      "aria-disabled": disabled,
      "aria-readonly": disabled,
      ...props,
      ref,
      children: icon ? /* @__PURE__ */ jsx(SvgIcon, { type: icon }) : children
    }
  );
});
CellButton.displayName = "CellButton";

export { Cell, CellButton };
